import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CheckmarkCircle02Icon } from '@hugeicons-pro/core-solid-rounded';
import { CheckmarkCircle04Icon } from '@hugeicons-pro/core-stroke-rounded';
import {
  Add01Icon,
  Archive03Icon,
  Cancel01Icon,
  CancelCircleIcon,
  Delete02Icon,
  Download01Icon,
  Link02Icon,
  SecurityCheckIcon,
  TaskEdit01Icon,
  TaskEdit02Icon,
  UserCheck01Icon,
  UserIcon,
  UserLove01Icon,
  WorkHistoryIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { MESSAGE_STATE_ERROR, MESSAGE_STATE_SUCCESS } from 'constants/message-app-state-contants';
import { Permissions } from 'constants/permissions';
import { getFetchBaseQueryErrorMessage } from 'helpers/api';
import {
  CheckAlertBadge,
  REFERENCE_CHECK_TYPES,
  getAlertLabels,
  getCheckAlertType,
  getWorstRefereeAlertType,
} from 'helpers/checkAlertBadge';
import { shouldPromptFcraConsent } from 'helpers/checks';
import { timeDifference } from 'helpers/date';
import { errorRouter } from 'helpers/error-router';
import {
  checkFlagSeverity,
  refereeWorstSeverity,
  sumFlagCounts,
  worstSeverity,
} from 'helpers/flagSeverity';
import { getCheckTypeIcon } from 'helpers/iconsHelper';
import { getRecentTagIds, saveRecentTagId } from 'helpers/recentTags';
import { useAccessControl } from 'hooks/access-control';
import { useDocumentTitle } from 'hooks/document-title';
import { useLocale } from 'hooks/locale';
import { useToast } from 'hooks/toast';
import { useApiUploadActions } from 'hooks/upload-actions';
import { useFCRAConsentModal } from 'hooks/use-fcra-consent-modal';
import AppPage from 'layouts/AppPage/AppPage';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import ReactGA from 'react-ga4';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { BrandSelectionModal } from './components/BrandSelectionModal/BrandSelectionModal';
import { ActionsMenu } from 'components/ActionsMenu/ActionsMenu';
import Alert from 'components/Alert/Alert';
import CopyEmail from 'components/CopyEmail/CopyEmail';
import { IconComponent } from 'components/Icon/IconComponent';
import { IconButton } from 'components/IconButton/IconButton';
import StatusPill from 'components/Pills/StatusPills';
import { TabSelect } from 'components/Tabs/Tabs';
import { TagSearchDropdown } from 'components/TagSearchDropdown/TagSearchDropdown';
import { CustomToast } from 'components/Toast/CustomToast';

import { messagePopUp } from 'api/app-slice';
import { billingEntityOptionSetFromStructure, useGetBillingStructureQuery } from 'api/billing-api';
import { useLazyGetBrandQuery } from 'api/brands-api';
import { getBrandUploads, getBrands, selectBrands } from 'api/brands-slice';
import {
  useCancelCandidateCheckMutation,
  useCreateCandidateCheckMutation,
  useLazyGetCandidateCheckResultQuery,
  useLazyGetCustomCandidateCheckResultQuery,
  useUpdateCandidateCheckMutation,
} from 'api/candidate-checks-api';
import { getCustomFields, selectCandidateCustomFields } from 'api/candidate-custom-fields-slice';
import {
  candidatesApi,
  useAddCandidateHistoryMutation,
  useAddTagToCandidateMutation,
  useCancelCandidateChecksMutation,
  useGetCandidateChecksQuery,
  useGetCandidateEmployeeQuery,
  useGetCandidateFlagCountsQuery,
  useGetCandidateRefereesQuery,
  useGetUserConsentsQuery,
  useLazyGetCandidateReportQuery,
  useLazyGetCombinedPdfReportQuery,
  useRemoveTagFromCandidateMutation,
  useSendCandidateRequestEmailMutation,
} from 'api/candidates-api';
import { deleteCandidate, getCandidate, updateCandidate } from 'api/candidates-slice';
import { selectCandidates } from 'api/candidates-slice';
import { useGetCheckTypesQuery } from 'api/check-types-api';
import { selectCompanyChecks } from 'api/company-checks-slice';
import { selectCompany } from 'api/company-slice';
import { getForms } from 'api/forms-slice';
import { selectForms } from 'api/forms-slice';
import {
  useCreateRefereeMutation,
  useDeleteRefereeMutation,
  useLazyGetCustomPdfReportQuery,
  useLazyGetPdfReportQuery,
  useSendRefereeRequestEmailMutation,
  useSendReplacementRequestMutation,
  useUpdateRefereeMutation,
} from 'api/referees-api';
import { useUpdateSubCheckMutation } from 'api/sub-checks-api';
import { apiErrorMessage, createTag, getTags, selectTags } from 'api/tags-slice';
import { useGetCompanyUsersQuery, useGetUserTeamsQuery } from 'api/user-api';
import { selectPermissions } from 'api/user-permissions-slice';
import { selectUser } from 'api/user-slice';

import { AddCheckModal } from '../../components/AddCheckModal/AddCheckModal';
import { ConsentsList } from '../Common/Consents/ConsentsList';
import styles from './Check.module.scss';
// ─────────────────────────────────────────────────────────────────────────────
import { Candidate } from './tabs/Candidate/Candidate';
import { Checks } from './tabs/Checks/Checks';
import { History } from './tabs/History/History';
import { Questionnaire } from './tabs/Questionnaire/Questionnaire';
import { Referees } from './tabs/Referees/Referees';
import { Reference } from './tabs/Reference/Reference';
import { Reports } from './tabs/Reports/Reports';

const Check = () => {
  const { id, tab, target } = useParams();

  const user = useSelector(selectUser);
  const company = useSelector(selectCompany);
  const companyChecks = useSelector(selectCompanyChecks);
  const { data: billingStructure } = useGetBillingStructureQuery(undefined, {
    skip: !!company && company.billing_mode !== 'entity',
  });
  const billingEntityOptionSet = billingEntityOptionSetFromStructure(billingStructure);
  const forms = useSelector(selectForms);
  const candidates = useSelector(selectCandidates);
  const tags = useSelector(selectTags);
  // Candidate endpoint returns tags as {id, name} without color — enrich from the tags store
  const tagColorMap = useMemo(() => Object.fromEntries(tags.map(t => [t.id, t.color])), [tags]);
  const permissions = useSelector(selectPermissions);
  const customFields = useSelector(selectCandidateCustomFields);
  const brands = useSelector(selectBrands);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasAccess = useAccessControl(permissions);
  const showToast = useToast();

  const { data: flagCounts } = useGetCandidateFlagCountsQuery(id, {
    refetchOnMountOrArgChange: true,
  });
  const { getUpload, signUpload, createUpload, deleteUpload } = useApiUploadActions();

  const { data: checksData, refetch: refetchCandidateChecks } = useGetCandidateChecksQuery(id, {
    refetchOnMountOrArgChange: true,
  });
  const { data: refereesData } = useGetCandidateRefereesQuery(id, {
    refetchOnMountOrArgChange: true,
  });
  const { data: userConsentsData } = useGetUserConsentsQuery(id, {
    refetchOnMountOrArgChange: true,
  });
  const { data: employee, refetch: refetchEmployee } = useGetCandidateEmployeeQuery(id, {
    refetchOnMountOrArgChange: true,
  });
  const { data: checkTypes } = useGetCheckTypesQuery();
  const { dateSlashFormat } = useLocale(company?.locale);
  const { data: teams } = useGetUserTeamsQuery(user.id);

  const onGetBrandUploads = brandId => dispatch(getBrandUploads(brandId));

  const [getCandidateCheckResult] = useLazyGetCandidateCheckResultQuery();
  const [getCustomCandidateCheckResult] = useLazyGetCustomCandidateCheckResultQuery();
  const [getBrand] = useLazyGetBrandQuery();

  const [addTag] = useAddTagToCandidateMutation();
  const [removeTag] = useRemoveTagFromCandidateMutation();
  const [cancelCandidateChecks] = useCancelCandidateChecksMutation();
  const [sendCandidateRequestEmail] = useSendCandidateRequestEmailMutation();
  const [sendRefereeRequestEmail] = useSendRefereeRequestEmailMutation();
  const [sendReplacementRequest] = useSendReplacementRequestMutation();
  const [addCandidateHistory] = useAddCandidateHistoryMutation();
  const [updateReferee] = useUpdateRefereeMutation();
  const [createReferee] = useCreateRefereeMutation();
  const [createCandidateCheck] = useCreateCandidateCheckMutation();
  const [cancelCandidateCheck] = useCancelCandidateCheckMutation();
  const [updateCandidateCheck] = useUpdateCandidateCheckMutation();
  const [updateSubCheck] = useUpdateSubCheckMutation();
  const [deleteReferee] = useDeleteRefereeMutation();

  const [getCombinedPdfReport] = useLazyGetCombinedPdfReportQuery();
  const [getPdfReport] = useLazyGetPdfReportQuery();
  const [getCustomPdfReport] = useLazyGetCustomPdfReportQuery();
  const [getCandidateReport] = useLazyGetCandidateReportQuery();

  const onMessagePopUp = (text, state, hide = true) =>
    dispatch(messagePopUp({ text, state, hide }));

  const [loading, setLoading] = useState(false);
  const [candidate, setCandidate] = useState();
  const candidateRef = useRef(candidate);
  candidateRef.current = candidate;
  const pendingTagToggles = useRef(new Set());
  const [checks, setChecks] = useState([]);
  const [navigation, setNavigation] = useState({ previous: null, next: null });
  const [showTagsMenu, setShowTagsMenu] = useState(false);
  const addTagTriggerRef = useRef(null);
  const [showNewChecksModal, setShowNewChecksModal] = useState(false);
  const [tabKey, setTabKey] = useState('candidate');

  const [referenceCheck, setReferenceCheck] = useState(null);
  const [customReferenceCheck, setCustomReferenceCheck] = useState(null);
  const [questionnaireCheck, setQuestionnaireCheck] = useState(null);
  const [referees, setReferees] = useState([]);
  const [refereesCompleted, setRefereesCompleted] = useState(0);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [emailLinkedOpened, setEmailLinkOpened] = useState(false);
  const [brand, setBrand] = useState();
  const [alertDelete, setAlertDelete] = useState(false);
  const [alertEmployed, setAlertEmployed] = useState(false);
  const [showBrandModal, setShowBrandModal] = useState(false);

  const { t } = useTranslation();

  const handleConsentSubmit = (data, checksData) => {
    handleNewChecks({ ...checksData, consents: data });
  };

  const { showModal: showFCRAConsentModal, FCRAConsentModal } = useFCRAConsentModal({
    onConsentSubmit: handleConsentSubmit,
  });

  useDocumentTitle(candidate ? candidate.name : 'Loading...');

  const userConsents = useMemo(() => {
    if (!userConsentsData) return [];

    const byKey = {};
    userConsentsData.forEach(consent => {
      const key = `${consent.consent_key}-${consent.consent_version}`;
      if (!byKey[key] || (!byKey[key].upload_id && consent.upload_id)) byKey[key] = consent;
    });
    return Object.values(byKey).map(consent => ({
      key: `${consent.consent_key}-${consent.consent_version}`,
      title: t(`users.consent.${consent.consent_key}.title`),
      content: consent.consent_text,
      upload: consent.upload ?? null,
    }));
  }, [userConsentsData, t]);

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: window.location.pathname,
      title: 'Candidate check page',
    });
  }, []);

  useEffect(() => {
    if (checksData) setChecks(checksData);
  }, [checksData]);
  useEffect(() => {
    if (refereesData) setReferees(refereesData);
  }, [refereesData]);

  // Load candidate
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [, , brandsResult] = await Promise.all([
          dispatch(getTags()),
          dispatch(getCustomFields()),
          dispatch(getBrands()),
        ]);

        const candidateAction = await dispatch(getCandidate(id));
        if (getCandidate.rejected.match(candidateAction)) {
          navigate(errorRouter(candidateAction.payload));
          return;
        }
        const candidate = candidateAction.payload;
        setCandidate(candidate);

        try {
          const brand = await getBrand(candidate.brand_id).unwrap();
          setBrand(brand);
        } catch (error) {
          console.error('Failed to get brand:', error);
          const loadedBrands = brandsResult?.payload || [];
          if (loadedBrands.length > 1) {
            setShowBrandModal(true);
          } else if (loadedBrands.length === 1) {
            await handleBrandSelection(loadedBrands[0], false);
          }
        }
      } catch (error) {
        navigate(errorRouter(error));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  // Set navigation
  useEffect(() => {
    if (candidates.length < 1) return;

    let ids = candidates.map(candidate => candidate.id);
    let index = ids.findIndex(i => i == id);
    if (index < 0) return;

    let previous = index === 0 ? null : ids[index - 1];
    let next = index === ids.length - 1 ? null : ids[index + 1];

    setNavigation({ previous, next });
  }, [id, candidates.length]);

  // Set referees completed
  useEffect(() => {
    setRefereesCompleted(referees.filter(referee => !!referee.completed_at).length);
  }, [referees.length]);

  // Set special checks
  useEffect(() => {
    if (checks?.length < 1) return;

    setReferenceCheck(checks.find(check => check.type === 'reference' && check.canceled == false));
    setQuestionnaireCheck(
      checks.filter(check => check.type === 'candidate_questionnaire' && check.canceled == false),
    );
    setCustomReferenceCheck(
      checks.find(check => check.type === 'custom_reference' && check.canceled == false),
    );
  }, [checks]);

  const canAccessResults = useCallback(
    (checkType, view = false) => {
      return companyChecks?.find(companyCheck => companyCheck.type === checkType)
        ?.has_sensitive_results
        ? hasAccess(Permissions.CandidateChecksSensitiveResults)
        : view
          ? hasAccess(Permissions.CandidateChecksResultsRead)
          : hasAccess(Permissions.CandidateChecksResultsDownload);
    },
    [companyChecks, hasAccess],
  );

  const setTabPath = key => {
    setTabKey(key);
    navigate(`/check/${id}/${key}`);
  };

  useEffect(() => {
    if (tab) {
      if (tab === 'reference') {
        setShowNewChecksModal(false);
      }
      setTabKey(tab);
    }
  }, [tab]);

  // TEMPLATE

  const handleToggleTag = async tagId => {
    const numTagId = Number(tagId);
    // Ignore repeat toggles of the same tag while one is in flight so rapid
    // double-clicks can't both read the same pre-update state and desync.
    if (pendingTagToggles.current.has(numTagId)) return;
    const tagsOnCandidate = candidateRef.current?.tags || [];
    const alreadyApplied = tagsOnCandidate.some(t => Number(t.id) === numTagId);
    // Intentionally no global setLoading here: it mounts the full-page overlay
    // (z-index 10000) over the portal tag dropdown (9999) and breaks multi-select.
    // Optimistic setCandidate updates + the pendingTagToggles guard cover feedback
    // and prevent duplicate in-flight toggles.
    pendingTagToggles.current.add(numTagId);
    try {
      if (alreadyApplied) {
        const { error } = await removeTag({ id, tagId: numTagId });
        if (!error) {
          setCandidate(prev => ({
            ...prev,
            tags: prev.tags.filter(t => Number(t.id) !== numTagId),
          }));
        } else {
          toast.custom(toastInstance => (
            <CustomToast
              t={toastInstance}
              title="Could not remove tag"
              message={
                getFetchBaseQueryErrorMessage(error) || 'Please try again or contact support.'
              }
              type="error"
            />
          ));
        }
      } else {
        const { data: tag, error } = await addTag({ id, tagId: numTagId });
        if (!error && tag) {
          setCandidate(prev => ({
            ...prev,
            tags: prev.tags.some(t => Number(t.id) === numTagId) ? prev.tags : [...prev.tags, tag],
          }));
          saveRecentTagId(numTagId);
        } else {
          toast.custom(toastInstance => (
            <CustomToast
              t={toastInstance}
              title="Could not add tag"
              message={
                getFetchBaseQueryErrorMessage(error) || 'Please try again or pick another tag.'
              }
              type="error"
            />
          ));
        }
      }
    } finally {
      pendingTagToggles.current.delete(numTagId);
    }
  };

  const handleCreateAndAddTag = async ({ name, color }) => {
    // No global setLoading: the full-page overlay would cover the portal tag
    // dropdown and break multi-select. The optimistic setCandidate update below
    // provides feedback for the created-and-added tag.
    try {
      const newTag = await dispatch(createTag({ name, color })).unwrap();
      if (!newTag?.id) {
        throw new Error('Tag was created but no id was returned.');
      }
      const tagIdNum = Number(newTag.id);
      const { data: tag, error } = await addTag({ id, tagId: tagIdNum });
      if (error || !tag) {
        toast.custom(toastInstance => (
          <CustomToast
            t={toastInstance}
            title="Tag created but not applied"
            message={
              getFetchBaseQueryErrorMessage(error) ||
              'Could not add the tag to this candidate. Try adding it from the tag menu.'
            }
            type="error"
          />
        ));
        return;
      }
      setCandidate(prev => ({
        ...prev,
        tags: prev.tags.some(t => Number(t.id) === tagIdNum) ? prev.tags : [...prev.tags, tag],
      }));
      saveRecentTagId(tagIdNum);
    } catch (e) {
      const errMsg = apiErrorMessage(e, 'Please try again.');
      toast.custom(toastInstance => (
        <CustomToast t={toastInstance} title="Could not create tag" message={errMsg} type="error" />
      ));
      // Re-throw so the dropdown keeps the typed name for retry.
      throw e;
    }
  };

  const hanleCandidateCancelation = async () => {
    setShowCancelModal(prevState => !prevState);
    setLoading(true);
    const { data: result } = await cancelCandidateChecks(id);
    setCandidate(result);
    await refetchCandidateChecks();
    setLoading(false);
  };

  const handleArchiveToggle = async () => {
    setLoading(true);
    const { payload } = await dispatch(
      updateCandidate({ id, params: { archived: !candidate.archived } }),
    );
    setCandidate(payload);
    setLoading(false);
  };

  const handleCheckDeletion = async () => {
    setLoading(true);
    await dispatch(deleteCandidate(id));
    setLoading(false);
    navigate('/dashboard');
  };

  const handleCandidateDeletion = () => {
    setAlertDelete(true);
  };

  const onCheckDelete = async () => {
    setAlertDelete(false);
    setLoading(true);
    await dispatch(deleteCandidate(id));
    dispatch(messagePopUp({ text: 'Candidate deleted', state: MESSAGE_STATE_SUCCESS, hide: true }));
    setLoading(false);
    navigate('/dashboard');
  };

  const handleDownloadAll = async () => {
    setLoading(true);
    await getCandidateReport(candidate).unwrap();
    setLoading(false);
  };

  const handleTagDeletion = async tagId => {
    setLoading(true);
    const tagIdNum = Number(tagId);
    try {
      const { error } = await removeTag({ id, tagId: tagIdNum });
      if (!error) {
        setCandidate(prev => ({
          ...prev,
          tags: prev.tags.filter(tag => Number(tag.id) !== tagIdNum),
        }));
      } else {
        toast.custom(toastInstance => (
          <CustomToast
            t={toastInstance}
            title="Could not remove tag"
            message={getFetchBaseQueryErrorMessage(error) || 'Please try again or contact support.'}
            type="error"
          />
        ));
      }
    } finally {
      setLoading(false);
    }
  };

  const hanldeSendCandidateRequestEmail = async (templateId = null) => {
    try {
      setLoading(true);
      await sendCandidateRequestEmail({ id, templateId });
      showToast(
        'Notification sent. Good job!',
        'The candidate will be notified with your updates. Have a great day!',
      );
    } catch (error) {
      showToast(
        'Error sending notification',
        'Please try again or if it continues, please contact support.',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  const hanldeSendRefereeRequestEmail = async (refereeId, template_id) => {
    setLoading(true);
    const { data: result } = await sendRefereeRequestEmail({ id: refereeId, template_id });
    if (result) onMessagePopUp('New referee request Sent', MESSAGE_STATE_SUCCESS);
    else onMessagePopUp('Request not sent', MESSAGE_STATE_ERROR);
    setLoading(false);
  };

  const handleUpdateCandidate = async params => {
    try {
      setLoading(true);
      const { payload: candidate } = await dispatch(updateCandidate({ id, params }));
      setCandidate(candidate);
      setLoading(false);
      showToast(
        'Awesome! - Candidate updated successfully',
        'Candidate details updated successfully in the system. Have a great day!',
      );
    } catch (error) {
      showToast(
        'Error updating candidate',
        'Please try again or if it continues, please contact support.',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGetPdfReport = async (id, filename, combined = false) => {
    setLoading(true);
    try {
      if (combined) await getCombinedPdfReport({ id, filename }).unwrap();
      else await getPdfReport({ id, filename }).unwrap();
    } catch {
      showToast(
        'Error downloading report',
        'Please try again or if it continues, please contact support.',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGetCustomPdfReport = async (id, filename) => {
    setLoading(true);
    try {
      await getCustomPdfReport({ id, filename }).unwrap();
    } catch {
      showToast(
        'Error downloading report',
        'Please try again or if it continues, please contact support.',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  const getCombinedReport = async check => {
    setLoading(true);
    await getCandidateCheckResult({ check, candidate });
    setLoading(false);
  };

  const getCombinedCustomReport = async check => {
    setLoading(true);
    try {
      await getCustomCandidateCheckResult({ check, candidate }).unwrap();
    } catch {
      showToast(
        'Error downloading report',
        'Please try again or if it continues, please contact support.',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCheckSubmit = async checksData => {
    const hasFCRA = shouldPromptFcraConsent(
      checksData?.checks || [],
      companyChecks,
      checkTypes,
      company?.country,
    );

    if (hasFCRA) {
      showFCRAConsentModal(checksData);
    } else {
      handleNewChecks(checksData);
    }
  };

  const handleNewChecks = async checksData => {
    try {
      setShowNewChecksModal(false);
      setLoading(true);
      const candidateChecks = await createCandidateCheck({
        candidateId: candidate.id,
        data: {
          ...checksData.checks,
          template_id: checksData.template_id ?? null,
          ...(checksData?.consents && { user_consents: checksData.consents }),
        },
      }).unwrap();
      setChecks(candidateChecks);
      setCandidate({ ...candidate, submitted_at: null, completed_at: null, status: 'candidate' });

      showToast(
        'Checks are added!',
        `Check created successfully for ${candidate.first_name} ${candidate.last_name}. Have a great day!`,
      );
    } catch (error) {
      const errorMessage = error?.data?.error?.log || 'Something went wrong';
      showToast(
        `Oops - ${errorMessage}`,
        `Please try again or if it continues, please contact support.`,
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCheck = async candidateCheckId => {
    setLoading(true);
    try {
      const updatedCheck = await cancelCandidateCheck({ id: candidateCheckId }).unwrap();
      setChecks(checks.map(check => (check.id === updatedCheck.id ? updatedCheck : check)));
    } catch (error) {
      const errorMessage = error?.data?.error?.log || 'Something went wrong';
      showToast(
        `Oops - ${errorMessage}`,
        'Please try again or if it continues, please contact support.',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCheckUpdate = async (candidateCheckId, data) => {
    setLoading(true);
    try {
      const updatedCheck = await updateCandidateCheck({ id: candidateCheckId, data }).unwrap();
      setChecks(checks.map(check => (check.id === updatedCheck.id ? updatedCheck : check)));
      setCustomReferenceCheck(prev => (prev?.id === updatedCheck.id ? updatedCheck : prev));
    } catch (error) {
      const errorMessage = error?.data?.error?.log || 'Something went wrong';
      showToast(
        `Oops - ${errorMessage}`,
        'Please try again or if it continues, please contact support.',
        'error',
      );
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleSubCheckUpdate = async (subCheckId, data) => {
    setLoading(true);
    const { data: updatedSubCheck } = await updateSubCheck({ id: subCheckId, params: data });
    setChecks(
      checks.map(check => {
        if (check.id !== updatedSubCheck.candidate_check_id) {
          return check;
        }

        const subChecks = check.sub_checks.map(subCheck =>
          subCheck.id === updatedSubCheck.id ? updatedSubCheck : subCheck,
        );
        return { ...check, sub_checks: subChecks };
      }),
    );
    setLoading(false);
  };

  const handleShowNewChecksModal = async () => {
    setLoading(true);
    await dispatch(getForms());
    setShowNewChecksModal(true);
    setLoading(false);
  };

  const invalidateRefereeData = () =>
    dispatch(candidatesApi.util.invalidateTags(['CandidateFlags', 'CandidateReferees']));

  const handleCreateReferee = async data => {
    setLoading(true);
    const { data: referee } = await createReferee(data);
    if (referee) setReferees([...referees, referee]);
    invalidateRefereeData();
    setLoading(false);
    return referee;
  };

  const handleRefereeUpdate = async (id, data) => {
    setLoading(true);
    try {
      const updatedReferee = await updateReferee({ id, data }).unwrap();
      setReferees(
        referees.map(referee => (referee.id === updatedReferee.id ? updatedReferee : referee)),
      );
      refetchCandidateChecks();
      invalidateRefereeData();
    } catch (error) {
      onMessagePopUp(error?.data?.error?.log || 'Failed to update referee', MESSAGE_STATE_ERROR);
    }
    setLoading(false);
  };

  const handleRefereeDeleted = async id => {
    setLoading(true);
    await deleteReferee(id);
    setReferees(referees.filter(referee => referee.id !== id));
    refetchCandidateChecks();
    invalidateRefereeData();
    setLoading(false);
  };

  const handleRefereeCancelation = async id => {
    setLoading(true);
    await updateReferee({ id, data: { canceled: true } });
    setReferees(referees.filter(referee => referee.id !== id));
    invalidateRefereeData();
    setLoading(false);
  };

  const handleRefereeReplacement = async (id, template_id, message) => {
    setLoading(true);
    const { data: replacementReferee } = await sendReplacementRequest({ id, template_id, message });

    setReferees([
      ...referees.map(referee =>
        referee.id === id ? { ...referee, replaced_by: replacementReferee.id } : referee,
      ),
      replacementReferee,
    ]);

    refetchCandidateChecks();
    invalidateRefereeData();
    const { payload: refreshedCandidate } = await dispatch(getCandidate(candidate.id));
    if (refreshedCandidate) setCandidate(refreshedCandidate);
    onMessagePopUp('Referee replacement request sent', MESSAGE_STATE_SUCCESS);
    setLoading(false);
  };

  const handleBrandSelection = async (selectedBrand, displayMessage = true) => {
    setLoading(true);
    try {
      const { payload: updatedCandidate } = await dispatch(
        updateCandidate({
          id,
          params: { brand_id: selectedBrand.id },
        }),
      );
      setCandidate(updatedCandidate);
      setBrand(selectedBrand);
      setShowBrandModal(false);
      if (displayMessage) {
        onMessagePopUp('Brand updated successfully', MESSAGE_STATE_SUCCESS);
      }
    } catch (error) {
      onMessagePopUp('Failed to update brand', MESSAGE_STATE_ERROR);
    }
    setLoading(false);
  };

  const handleMarkAsEmployed = async () => {
    setLoading(true);
    await handleUpdateCandidate({ is_hired: true });
    refetchEmployee();
    setAlertEmployed(false);
    showToast('Candidate marked as employed', 'Candidate marked as employed successfully');

    setLoading(false);
  };

  const { data: companyUsers } = useGetCompanyUsersQuery(user?.id, {
    refetchOnMountOrArgChange: true,
  });

  return (
    <AppPage loading={loading}>
      <BrandSelectionModal
        visible={showBrandModal}
        onClose={() => setShowBrandModal(false)}
        brands={brands}
        onUpdate={handleBrandSelection}
        loading={loading}
      />
      <AddCheckModal
        brands={brands}
        company={company}
        companyChecks={companyChecks}
        brand={brand}
        existingChecks={checks}
        forms={forms}
        visible={showNewChecksModal}
        useDocuments
        getBrandUploads={onGetBrandUploads}
        onSubmit={handleCheckSubmit}
        candidate={candidate}
        onClose={() => setShowNewChecksModal(false)}
        className={styles.addModalCheck}
        getUpload={getUpload}
        signUpload={signUpload}
        createUpload={createUpload}
        deleteUpload={deleteUpload}
        allowUploads
      />
      {FCRAConsentModal}
      <Alert
        show={alertDelete}
        title={t('check.deleteCandidateTitle')}
        message={t('check.deleteCandidateMessage')}
        ok="Delete"
        onOk={params => {
          setAlertDelete(false);
          onCheckDelete(params);
        }}
        cancel="Cancel"
        type="error"
        showConfirmationInput
        icon={Delete02Icon}
        onCancel={() => setAlertDelete(false)}
      />
      <Alert
        show={alertEmployed}
        title="Confirm employment"
        message="This candidate will be added to your employee list and made available for renewal checks."
        ok="Mark as Employed"
        onOk={handleMarkAsEmployed}
        cancel="Cancel"
        type="success"
        icon={CheckmarkCircle04Icon}
        onCancel={() => setAlertEmployed(false)}
      />
      {candidate && (
        <div className={styles.root}>
          {!candidate.completed_at &&
            hasAccess(Permissions.CandidateWrite) &&
            !candidate.canceled && (
              <Alert
                show={showCancelModal}
                title={t('check.cancelCandidateTitle')}
                ok={t('common.cancel')}
                cancel={t('common.close')}
                onOk={params => {
                  hanleCandidateCancelation(params);
                }}
                type="error"
                showConfirmationInput
                icon={Cancel01Icon}
                onCancel={() => setShowCancelModal(false)}
              />
            )}
          <div className="flex flex-col gap-4 mx-[30px] mt-8">
            <p className={'text-text-tertiary text-[10px] font-light'}>
              <Link className="u-link dark" to="/dashboard">
                DASHBOARD
              </Link>{' '}
              / CHECK FOR
            </p>
            <div className="flex flex-col">
              {candidate.volunteer && (
                <span className="text-xl font-regular pt-4 py-10 px-10 bg-pink mt-8 text-neutral-1 rounded-t-3xl flex items-center">
                  <HugeiconsIcon
                    icon={UserLove01Icon}
                    size="20"
                    strokeWidth="1.5"
                    className="mr-2"
                  />{' '}
                  This candidate is a volunteer.
                </span>
              )}
              <div
                className={cn(
                  styles.header,
                  candidate.volunteer ? 'relative top-[-18px]' : '',
                  'shadow-sm',
                )}>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="d-flex justify-content-start align-items-center">
                      <h1 className="title-2">{candidate.name}</h1>
                      <ActionsMenu className={styles.showMedium} small>
                        {hasAccess(Permissions.CandidateWrite) && !candidate.canceled && (
                          <ActionsMenu.Item
                            icon={CancelCircleIcon}
                            newIconLibrary
                            title={t('check.actions.cancelCandidateButton')}
                            onClick={() => setShowCancelModal(prevState => !prevState)}
                            disabled={candidate.completed_at}
                          />
                        )}
                        {hasAccess(Permissions.CandidateWrite) && (
                          <ActionsMenu.Item
                            icon={Archive03Icon}
                            newIconLibrary
                            title={
                              candidate.archived
                                ? t('check.actions.restore')
                                : t('check.actions.archive')
                            }
                            onClick={handleArchiveToggle}
                          />
                        )}
                        {hasAccess(Permissions.CandidateWrite) && (
                          <ActionsMenu.Item
                            icon={TaskEdit02Icon}
                            newIconLibrary
                            title={t('check.actions.addNote')}
                            onClick={() => {
                              setTabKey('history');
                              navigate(`/check/${id}/history/new`);
                            }}
                          />
                        )}
                        {hasAccess(Permissions.CandidateWrite) && (
                          <ActionsMenu.Item
                            icon={Delete02Icon}
                            newIconLibrary
                            title={t('check.actions.delete')}
                            onClick={handleCheckDeletion}
                          />
                        )}
                        {hasAccess(Permissions.CandidateChecksResultsDownload) &&
                          checks.some(check => !!check.completed_at && !check.canceled) &&
                          !candidate.canceled && (
                            <ActionsMenu.Item
                              icon={Download01Icon}
                              newIconLibrary
                              title={t('check.actions.downloadAll')}
                              onClick={handleDownloadAll}
                            />
                          )}
                      </ActionsMenu>
                      <div className={cn(styles.hideMedium, 'flex items-center gap-4 ml-8')}>
                        {hasAccess(Permissions.CandidateWrite) && !candidate.canceled && (
                          <OverlayTrigger
                            overlay={<Tooltip>{t('check.actions.cancelCandidateButton')}</Tooltip>}>
                            <HugeiconsIcon
                              icon={CancelCircleIcon}
                              size="24"
                              strokeWidth="1.5"
                              className="text-neutral-6 cursor-pointer"
                              onClick={() => setShowCancelModal(prevState => !prevState)}
                            />
                          </OverlayTrigger>
                        )}
                        {hasAccess(Permissions.CandidateWrite) &&
                          company.renewal_dashboard_enabled && (
                            <OverlayTrigger
                              overlay={
                                <Tooltip>
                                  {candidate.is_hired
                                    ? t('check.actions.navigateToEmployee')
                                    : t('check.actions.markCandidateAsEmployeed')}
                                </Tooltip>
                              }>
                              <HugeiconsIcon
                                icon={CheckmarkCircle02Icon}
                                size="24"
                                primaryColor={candidate.is_hired ? '#039d41' : '#878787'}
                                strokeWidth="0.5"
                                className="cursor-pointer"
                                onClick={() =>
                                  candidate.is_hired
                                    ? navigate(`/employee/${employee.id}`)
                                    : setAlertEmployed(true)
                                }
                              />
                            </OverlayTrigger>
                          )}
                        {hasAccess(Permissions.CandidateWrite) && (
                          <OverlayTrigger
                            overlay={<Tooltip>{t('check.actions.addHistoryRecord')}</Tooltip>}>
                            <HugeiconsIcon
                              icon={TaskEdit02Icon}
                              size="24"
                              strokeWidth="1.5"
                              className="text-neutral-6 cursor-pointer"
                              onClick={() => {
                                setTabKey('history');
                                navigate(`/check/${id}/history/new`);
                              }}
                            />
                          </OverlayTrigger>
                        )}
                        <div
                          className={styles.emailLinkContainer}
                          data-testid="candidate-link-container">
                          <OverlayTrigger
                            overlay={<Tooltip>{t('check.actions.candidateLink')}</Tooltip>}>
                            <HugeiconsIcon
                              data-testid="candidate-link-icon"
                              icon={Link02Icon}
                              size="24"
                              strokeWidth="1.5"
                              className="text-neutral-6 cursor-pointer"
                              onClick={() => setEmailLinkOpened(prevState => !prevState)}
                            />
                          </OverlayTrigger>
                          <CopyEmail
                            open={emailLinkedOpened}
                            emailLink={`${process.env.REACT_APP_WEB_URL}/form_submission/candidate/${candidate.token}`}
                            title={t('check.copyCandidateLink')}
                            linkType="link"
                            setEmailLinkOpened={setEmailLinkOpened}
                            inputDataTestId="candidate-link-input"
                          />
                        </div>
                        {hasAccess(Permissions.CandidateChecksResultsDownload) &&
                          checks.some(check => !!check.completed_at && !check.canceled) &&
                          !candidate.canceled && (
                            <OverlayTrigger
                              overlay={<Tooltip>{t('check.actions.downloadAll')}</Tooltip>}>
                              <HugeiconsIcon
                                icon={Download01Icon}
                                size="24"
                                strokeWidth="1.5"
                                className="text-neutral-6 cursor-pointer"
                                onClick={handleDownloadAll}
                              />
                            </OverlayTrigger>
                          )}
                        {hasAccess(Permissions.CandidateWrite) && (
                          <OverlayTrigger
                            overlay={
                              <Tooltip>
                                {candidate.archived
                                  ? t('check.actions.restore')
                                  : t('check.actions.archive')}
                              </Tooltip>
                            }>
                            <HugeiconsIcon
                              icon={Archive03Icon}
                              size="24"
                              strokeWidth="1.5"
                              className="text-neutral-6 cursor-pointer"
                              active={candidate.archived}
                              onClick={handleArchiveToggle}
                            />
                          </OverlayTrigger>
                        )}
                        {hasAccess(Permissions.CandidateWrite) && (
                          <OverlayTrigger overlay={<Tooltip>{t('check.actions.delete')}</Tooltip>}>
                            <HugeiconsIcon
                              icon={Delete02Icon}
                              size="24"
                              strokeWidth="1.5"
                              className="text-red-2 cursor-pointer"
                              onClick={handleCandidateDeletion}
                            />
                          </OverlayTrigger>
                        )}
                      </div>
                    </div>
                  </div>
                  <div
                    className={cn(
                      styles.hideMobile,
                      'd-flex',
                      'align-items-start',
                      'justify-content-end',
                    )}>
                    <IconButton
                      icon="arrow-left"
                      className={cn(styles.arrowIcon, {
                        [styles['--disabled']]: !navigation.previous,
                      })}
                      tip={t('check.actions.previousCheck')}
                      onClick={() => navigate(`/check/${navigation.previous}/candidate`)}
                      disabled={!navigation.previous}
                    />
                    <IconButton
                      icon="arrow-right"
                      className={cn(styles.arrowIcon, {
                        [styles['--disabled']]: !navigation.next,
                      })}
                      tip={t('check.actions.nextCheck')}
                      onClick={() => navigate(`/check/${navigation.next}/candidate`)}
                      disabled={!navigation.next}
                    />
                  </div>
                </div>
                <div
                  className={cn(
                    styles.candidateDetails,
                    'd-flex',
                    'justify-content-start',
                    'align-items-start',
                    'u-padding-top',
                    'u-width-100',
                  )}>
                  <div className="d-flex justify-content-start align-items-center u-gap">
                    <StatusPill
                      status={candidate.status}
                      text={candidate.status}
                      large
                      capitalize
                      dataTestId="candidate-status-pill"
                    />
                    {checks.map(check => {
                      const checkType = companyChecks.find(
                        companyCheck => companyCheck.type === check.type,
                      )?.check_type;
                      if (!checkType) return;
                      const status = (() => {
                        switch (check.status) {
                          case 'completed':
                            return 'success';
                          case 'in-progress':
                          case 'submitted':
                            return 'info';
                          case 'pending':
                          case 'overdue':
                            return 'error';
                          case 'deferred':
                            return 'deferred';
                          case 'canceled':
                            return 'canceled';
                          default:
                            return 'warning';
                        }
                      })();
                      const isReference = REFERENCE_CHECK_TYPES.includes(check.type);
                      const activeReferees = referees.filter(
                        referee =>
                          referee &&
                          referee.candidate_check_id == check.id &&
                          !referee.replaced_by &&
                          !referee.canceled,
                      );
                      const severity = isReference
                        ? worstSeverity(
                            sumFlagCounts(
                              activeReferees.map(referee => ({
                                [refereeWorstSeverity(flagCounts, referee)]: 1,
                              })),
                            ),
                          )
                        : checkFlagSeverity(flagCounts, check);
                      const alertType = isReference
                        ? getWorstRefereeAlertType({
                            check,
                            referees: activeReferees,
                            severityOf: referee => refereeWorstSeverity(flagCounts, referee),
                          })
                        : getCheckAlertType({ check, checkType, severity });
                      const alertLabels = getAlertLabels({
                        check,
                        checkType,
                        severity,
                        ipMatch:
                          isReference &&
                          activeReferees.some(
                            referee => referee.warning_flag && referee.status === 'completed',
                          ),
                        t,
                      });
                      return (
                        <div key={check.id} className="u-relative">
                          <CheckAlertBadge type={alertType} />

                          <IconComponent
                            icon={getCheckTypeIcon(checkType?.slug)}
                            size="small"
                            type={status}
                            noBackground
                            tip={[`${checkType.title} ${check.status}`, ...alertLabels].join(' - ')}
                            onClick={() => navigate(`/check/${id}/background_checks`)}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className={cn(styles.infoFieldRow)}>
                    {candidate.reference_code && (
                      <div className={styles.infoField}>
                        <p className={styles.title}>{t('check.infoFields.referenceCode')}</p>
                        <p className={styles.value}>{candidate.reference_code}</p>
                      </div>
                    )}
                    <div className={styles.infoField}>
                      <p className={styles.title}>{t('check.infoFields.role')}</p>
                      <p className={styles.value}>{candidate.job_role}</p>
                    </div>
                    <div className={styles.infoField}>
                      <p className={styles.title}>{t('check.infoFields.department')}</p>
                      <p className={styles.value}>{candidate.department}</p>
                    </div>
                    {referenceCheck && (
                      <div className={styles.infoField}>
                        <p className={styles.title}>{t('check.infoFields.completed')}</p>
                        <p className={styles.value}>
                          {refereesCompleted} of{' '}
                          {referenceCheck.details?.referees ?? referees.length} Referees
                        </p>
                      </div>
                    )}
                    <div className={styles.infoField}>
                      <p className={styles.title}>{t('check.infoFields.createdOn')}</p>
                      <p className={styles.value}>
                        {candidate.created_at ? (
                          <Moment format={`${dateSlashFormat} @hh:mma`}>
                            {candidate.created_at}
                          </Moment>
                        ) : (
                          '-'
                        )}
                      </p>
                    </div>
                    <div className={styles.infoField}>
                      <p className={styles.title}>{t('check.infoFields.completedOn')}</p>
                      <p className={styles.value}>
                        {candidate.completed_at ? (
                          <Moment format={`${dateSlashFormat} @hh:mma`}>
                            {candidate.completed_at}
                          </Moment>
                        ) : (
                          '-'
                        )}
                      </p>
                    </div>
                    <div className={styles.infoField}>
                      <p className={styles.title}>{t('check.infoFields.timeToComplete')}</p>
                      <p className={styles.value}>
                        {candidate.completed_at
                          ? timeDifference(candidate.created_at, candidate.completed_at)
                          : '-'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className={styles.tags}>
                  {candidate.tags?.map((tag, index) => (
                    <div key={tag.id} className={styles.tag}>
                      <span
                        className={styles.tagDot}
                        style={{ '--tag-color': tagColorMap[tag.id] || tag.color }}
                      />
                      <span>{tag.name}</span>
                      {hasAccess(Permissions.CandidateWrite) && (
                        <span
                          className={styles.tagRemoveBtn}
                          onClick={() => handleTagDeletion(tag.id)}>
                          <HugeiconsIcon icon={Cancel01Icon} size="12" strokeWidth="2.5" />
                        </span>
                      )}
                    </div>
                  ))}
                  {hasAccess(Permissions.CandidateWrite) && (
                    <div
                      ref={addTagTriggerRef}
                      className={styles.addTagChip}
                      onClick={() => setShowTagsMenu(v => !v)}>
                      <HugeiconsIcon icon={Add01Icon} size="12" strokeWidth="2.5" />
                      {t('tags.addTag')}
                      <TagSearchDropdown
                        visible={showTagsMenu}
                        usePortal
                        triggerRef={addTagTriggerRef}
                        onClose={() => setShowTagsMenu(false)}
                        tags={tags}
                        allTagsForNameCheck={tags}
                        recentTags={getRecentTagIds()
                          .map(rid => tags.find(t => Number(t.id) === Number(rid)))
                          .filter(Boolean)}
                        selectedIds={(candidate.tags || []).map(t => Number(t.id))}
                        onSelect={handleToggleTag}
                        onCreate={
                          hasAccess(Permissions.TagsWrite) ? handleCreateAndAddTag : undefined
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <TabSelect
            id="accountTabs"
            onSelect={setTabPath}
            activeKey={tab}
            defaultActiveKey="candidate"
            newTabKey={tabKey}
            className="tabs pill-tabs mx-12 mt-12"
            mountOnEnter={true}
            unmountOnExit={true}>
            <TabSelect.Tab
              eventKey="history"
              title={t('check.tabs.history')}
              icon={WorkHistoryIcon}>
              <div className="flex-1 order-2 w-full bg-neutral-2 rounded-3xl border border-neutral-4 shadow-soft overflow-hidden">
                <History
                  key={candidate.id}
                  company={company}
                  candidate={candidate}
                  brand={brand}
                  referees={referees}
                  user={user}
                  companyChecks={companyChecks}
                  checks={checks}
                  setLoading={setLoading}
                  targetNewRecord={target === 'new'}
                  onCandidateRequest={hanldeSendCandidateRequestEmail}
                  onRefereeRequest={hanldeSendRefereeRequestEmail}
                  createRecord={addCandidateHistory}
                />
              </div>
            </TabSelect.Tab>
            <TabSelect.Tab eventKey="candidate" title={t('check.tabs.candidate')} icon={UserIcon}>
              <Candidate
                company={company}
                candidate={candidate}
                user={user}
                teams={teams}
                companyUsers={companyUsers}
                companyChecks={companyChecks}
                referees={referees}
                customFields={customFields}
                handleCandidateUpdate={handleUpdateCandidate}
                sendRequestEmail={hanldeSendCandidateRequestEmail}
                brand={brand}
                billingEntityOptionSet={billingEntityOptionSet}
              />
            </TabSelect.Tab>
            {referenceCheck && (
              <TabSelect.Tab
                eventKey="referees"
                title={t('check.tabs.referees')}
                icon={UserCheck01Icon}
                disabled={candidate.canceled}>
                <Referees
                  check={referenceCheck}
                  candidate={candidate}
                  company={company}
                  referees={referees}
                  setLoading={setLoading}
                  updateReferee={handleRefereeUpdate}
                  sendRequestEmail={hanldeSendRefereeRequestEmail}
                  targetId={target}
                  addReferee={handleCreateReferee}
                  deleteReferee={handleRefereeDeleted}
                  onMessagePopUp={onMessagePopUp}
                />
              </TabSelect.Tab>
            )}
            {referenceCheck && canAccessResults('reference', true) && (
              <TabSelect.Tab
                eventKey="reports"
                title={t('check.tabs.refereeReport')}
                icon={UserCheck01Icon}
                disabled={!referenceCheck || !refereesCompleted > 0}>
                <Reports
                  check={referenceCheck}
                  referees={referees}
                  candidate={candidate}
                  setLoading={setLoading}
                  getPdfReport={handleGetPdfReport}
                  targetId={target}
                  company={company}
                />
              </TabSelect.Tab>
            )}
            {customReferenceCheck && (
              <TabSelect.Tab
                eventKey="reference"
                title={t('check.tabs.referenceCheck')}
                icon={UserCheck01Icon}>
                <Reference
                  company={company}
                  candidate={candidate}
                  check={customReferenceCheck}
                  referees={referees}
                  brand={brand}
                  canAccessResults={canAccessResults('custom_reference')}
                  messagePopUp={onMessagePopUp}
                  addReferee={handleCreateReferee}
                  setLoading={setLoading}
                  getPdfReport={handleGetPdfReport}
                  getCustomPdfReport={handleGetCustomPdfReport}
                  getCombinedReport={getCombinedReport}
                  getCombinedCustomReport={getCombinedCustomReport}
                  updateReferee={handleRefereeUpdate}
                  updateCheck={handleCheckUpdate}
                  deleteReferee={handleRefereeDeleted}
                  cancelReferee={handleRefereeCancelation}
                  sendRequestEmail={hanldeSendRefereeRequestEmail}
                  sendReplacementRequest={handleRefereeReplacement}
                />
              </TabSelect.Tab>
            )}
            {questionnaireCheck &&
              questionnaireCheck?.length > 0 &&
              canAccessResults('candidate_questionnaire', true) && (
                <TabSelect.Tab
                  eventKey="questionnaire"
                  title={t('check.tabs.questionnaire')}
                  icon={TaskEdit01Icon}>
                  <Questionnaire
                    checks={questionnaireCheck}
                    candidate={candidate}
                    brand={brand}
                    company={company}
                    canAccessResults={canAccessResults('candidate_questionnaire')}
                    setLoading={setLoading}
                  />
                </TabSelect.Tab>
              )}
            <TabSelect.Tab
              eventKey="background_checks"
              title={t('check.tabs.backgroundChecks')}
              icon={SecurityCheckIcon}>
              <Checks
                company={company}
                candidate={candidate}
                checks={checks}
                user={user}
                companyChecks={companyChecks}
                setLoading={setLoading}
                showNewChecksModal={handleShowNewChecksModal}
                handleCancelCheck={handleCancelCheck}
                handleSubCheckUpdate={handleSubCheckUpdate}
                handleCheckUpdate={handleCheckUpdate}
              />
            </TabSelect.Tab>
            {userConsents && userConsents.length > 0 && (
              <TabSelect.Tab eventKey="consents" title={t('check.tabs.userConsents')}>
                <div className="flex-1 order-2 w-full bg-neutral-2 rounded-2xl border border-border-primary shadow-soft">
                  <ConsentsList
                    consents={userConsents}
                    consentsTitle={t('check.consent.userConsentsTitle')}
                    consentsDescription={t('check.consent.userConsentsDescription')}
                  />
                </div>
              </TabSelect.Tab>
            )}
          </TabSelect>
        </div>
      )}
    </AppPage>
  );
};

export default Check;
